import { type ReactNode, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import styles from './ui.module.css';

const WIDTH = 320;
const GAP = 8;

/**
 * A popover pinned to an anchor element, portalled to <body> and positioned
 * `fixed`.
 *
 * Both of those matter: the auth shell's form pane scrolls
 * (`overflow-y: auto`), so a popover placed beside the field in normal flow
 * gets clipped by it. Chakra portalled this for the same reason.
 *
 * Placement mirrors the Chakra original — to the right of the field from the
 * `md` breakpoint up, above it on narrow viewports where there is no room.
 */
export function AnchoredPopover({
  anchor,
  open,
  children,
}: {
  anchor: HTMLElement | null;
  open: boolean;
  children: ReactNode;
}) {
  const [box, setBox] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchor) {
      setBox(null);
      return;
    }
    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const toRight = window.matchMedia('(min-width: 48em)').matches;
      const fitsRight = rect.right + GAP + WIDTH <= window.innerWidth;
      setBox(
        toRight && fitsRight
          ? { top: rect.top + rect.height / 2, left: rect.right + GAP }
          : { top: rect.top - GAP, left: rect.left }
      );
    };
    place();
    // The pane scrolls and the viewport can resize while the field is focused.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, anchor]);

  if (!open || !box || typeof document === 'undefined') {
    return null;
  }

  const toRight = box.left > (anchor?.getBoundingClientRect().left ?? 0);
  return createPortal(
    <div
      className={styles.popover}
      data-ap-popover=""
      role="dialog"
      aria-label="Password requirements"
      style={{
        top: box.top,
        left: box.left,
        transform: toRight ? 'translateY(-50%)' : 'translateY(-100%)',
      }}
    >
      <span
        className={`${styles.popoverArrow} ${
          toRight ? styles.popoverArrowLeft : styles.popoverArrowBottom
        }`}
        aria-hidden="true"
      />
      <div className={styles.popoverBody}>{children}</div>
    </div>,
    document.body
  );
}
